import { FormattedMessage, useIntl } from 'react-intl';
import { getIsWhiteLabel, getWhiteLabelPrimaryColor } from '@/redux/modules/whiteLabel';
import { email as isValidEmail } from '@/utils/validation';
import { useAppSelector } from '@/redux/hooks';
import appTheme from '@liveauctioneers/caterwaul-components/lib/theme/theme';
import AutoCheckbox from '@liveauctioneers/caterwaul-components/lib/Checkbox/AutoCheckbox';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import classNames from 'classnames';
import Input from '@liveauctioneers/caterwaul-components/lib/Input/Input';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import React, { useState } from 'react';
import styles from './SignUpEmail.module.scss';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';

type Props = {
    bidderExists: boolean;
    error: string;
    noBodyMargins?: boolean;
    setEmail: Function;
    submitCreateAccount: Function;
    submitted: boolean;
};

const SignUpEmail = (props: Props) => {
    const { formatMessage } = useIntl();
    const { bidderExists, error, noBodyMargins = false, submitted } = props;

    const [agree, setAgree] = useState<boolean>(false);
    const [email, setEmailValue] = useState<string>('');
    const [emailValid, setEmailValid] = useState<boolean>(true);
    const [showError, setShowError] = useState<boolean>(false);

    const isWhiteLabel = useAppSelector(getIsWhiteLabel);
    const whiteLabelPrimaryColor = useAppSelector(getWhiteLabelPrimaryColor);

    const onEmailValueChange = (event: any) => {
        setEmailValue(event.target.value);
    };

    const onAgreeValueChange = (event: any) => {
        setAgree(event.currentTarget.checked);
    };

    const onSignUp = () => {
        const trimmedEmail = email.trim();
        const newEmailValid = isValidEmail(trimmedEmail);
        setEmailValid(newEmailValid);
        if (newEmailValid) {
            props.setEmail(trimmedEmail);
            props.submitCreateAccount(trimmedEmail, agree);
        }
    };

    const enterButton = () => {
        setShowError(true);
    };

    const exitButton = () => {
        setShowError(false);
    };

    let errorMessage: React.ReactNode = '';
    const errorOnPage = !emailValid || (error && !bidderExists);

    const tosLink = (
        <Link onClick={(e) => e.stopPropagation()} rel="noopener" target="_blank" to="/termsandconditions">
            <FormattedMessage id="signUpModal.termsLink" />
        </Link>
    );
    const privacyLink = (
        <Link onClick={(e) => e.stopPropagation()} rel="noopener" target="_blank" to="/privacy">
            <FormattedMessage id="signUpModal.privacyLink" />
        </Link>
    );
    const cookieLink = (
        <Link onClick={(e) => e.stopPropagation()} rel="noopener" target="_blank" to="/cookie-policy">
            <FormattedMessage id="signUpModal.cookieLink" />
        </Link>
    );

    if (!emailValid) {
        errorMessage = <FormattedMessage id="signUpModal.invalidEmail" />;
    } else if (error && !bidderExists) {
        errorMessage = <FormattedMessage id="signUpModal.error" />; // we could show the error
    }

    return (
        <div className={classNames(styles.body, { [styles.bodyMargin]: !noBodyMargins })}>
            <Input
                autoComplete="email"
                errorMessage={errorMessage}
                id="email"
                label={<FormattedMessage id="signUpModal.username" />}
                onChange={onEmailValueChange}
                testid="joinModalEmail"
                valid={!errorOnPage}
            />
            <AutoCheckbox
                className={styles['check-box']}
                label={formatMessage({ id: 'signUpModal.acceptText' })}
                labelDisplay={
                    <FormattedMessage id="signUpModal.acceptText" values={{ cookieLink, privacyLink, tosLink }} />
                }
                name="signUpEmailCheckbox"
                onChange={onAgreeValueChange}
            />

            <div className={styles.links} onMouseEnter={enterButton} onMouseLeave={exitButton}>
                <Button
                    className={styles.submit}
                    disabled={submitted || !email || !agree}
                    onClick={onSignUp}
                    primary
                    red={!isWhiteLabel || whiteLabelPrimaryColor === appTheme.colors.blue100}
                    testid="joinModalStartNow"
                >
                    {!submitted ? <FormattedMessage id="signUpModal.submit" /> : <Throbber colorWhite />}
                </Button>
            </div>
            {showError && email && !agree && (
                <Text caption colorError>
                    <FormattedMessage id="signUpModal.termsError" />
                </Text>
            )}
        </div>
    );
};

export default React.memo<Props>(SignUpEmail, isEqual);
